import { ParentGuardianDetails } from 'src/app/application/models/parent-guardian-details';
import { SchoolProgramPlacementDetails } from './../models/school-program-placement-details';
import { Component, HostListener, OnInit } from '@angular/core';
import { Summary } from '../models/summary.model';
import { ActivatedRoute, Router } from '@angular/router';
import { updateEmptyValuesForNullUndefined } from '../shared/utils/ModelUtils';
import { Country, Relationship, SchoolSubject, Term, SchoolDuration, School, Tab_Title, LookupData } from 'src/app/shared/models/lookup-data.model';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { ApiResponse, SingleResultApiResponse } from 'src/app/shared/models/api-response.model';
import { IApplicationStep } from '../models/application-step.interface';
import { slideInOut } from '../animations/slideInOut';
import { AccommodationWelfareDetails } from '../models/accommodation-welfare-details';
import { Title } from '@angular/platform-browser';
import { switchMap, tap } from 'rxjs/operators';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { of } from 'rxjs';

@Component({
  selector: 'online-app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  host: {
    class: 'application-section-component'
  },
  animations: [slideInOut]
})
export class SummaryComponent implements OnInit {

  summary: Summary;
  countries: Country[];
  relationships: Relationship[];
  schoolSubjects: SchoolSubject[];
  schools: School[];
  terms: Term[];
  durations: SchoolDuration[];
  applicationId: number;
  applicationSteps: IApplicationStep[];
  isPaymentReady = false;
  isSubmittedApplication = false;
  preferredSchools: string[];
  isCovid19VaccinationNeeded: boolean = false;
  deiApprovedCovid19Vaccines: LookupData[] = [];

  sectionVisibilityStatus = {
    'StudentDetails': false,
    'ParentGuardianDetails': false,
    'VisaAndOhscDetails': false,
    'SchoolProgramAndPlacement': false,
    'AccommodationAndWelfare': false,
    'EnglishProficiency': false,
    'SpecialCircumstances': false
  };

  sections = [
    { name: 'StudentDetails', visiblilityStatus: false, completionStatus: false },
    { name: 'ParentGuardianDetails', visiblilityStatus: false, completionStatus: false },
    { name: 'VisaAndOhscDetails', visiblilityStatus: false, completionStatus: false },
    { name: 'SchoolProgramAndPlacement', visiblilityStatus: false, completionStatus: false },
    { name: 'AccommodationAndWelfare', visiblilityStatus: false, completionStatus: false },
    { name: 'EnglishProficiency', visiblilityStatus: false, completionStatus: false },
    { name: 'SpecialCircumstances', visiblilityStatus: false, completionStatus: false }
  ];

  sectionsMappedByName = this.sections.reduce((o, section) => {
    o[section.name] = section;
    return o;
  }, {});

  englishLanguageTestMetadata = {
    IELTS: {
      code: 'IELTS',
      name: 'IELTS',
      reading: 'Reading',
      listening: 'Listening',
      speaking: 'Speaking',
      writing: 'Writing',
      total: 'Average Score'
    },
    TOEFL: {
      code: 'TOEFL',
      name: 'TOEFL iBT',
      reading: 'Reading',
      listening: 'Listening',
      speaking: 'Speaking',
      writing: 'Writing',
      total: 'Total Score'
    },
    AEAS: {
      code: 'AEAS',
      name: 'AEAS',
      reading: 'Reading Comprehension',
      listening: 'Listening',
      speaking: 'Speaking',
      writing: 'Written Essay',
      vocabulary: 'Vocabulary',
      total: 'Total Score'
    },
    EIKEN: {
      code: 'EIKEN',
      name: 'STEP Eiken',
      total: 'Grade'
    },
    CEFR: {
      code: 'CEFR',
      name: 'CEFR',
      total: 'Level'
    },
    OTHER: {
      code: 'OTHER',
      total: 'Grade / Average Score'
    }
  };

  constructor(private activatedRoute: ActivatedRoute,
    private router: Router,
    private applicationService: ApplicationService,
    private lookupService: LookupService,
    private titleService: Title) {

    this.activatedRoute.parent.params.subscribe(params => {
      this.applicationId = +params['id'];
    });

  }

  ngOnInit() {
    if(localStorage.getItem("isAgent") == "true"){
      this.titleService.setTitle(Tab_Title.Agent_Summary_Title);
    }else{
      this.titleService.setTitle(Tab_Title.Summary_Title);
    }
    this.summary = this.activatedRoute.snapshot.data['summary'];
    //console.log("Summary data from db is: -->", this.summary);

    this.countries = this.activatedRoute.snapshot.data['countries'];
    this.relationships = this.activatedRoute.snapshot.data['relationshipTypes'];
    this.schoolSubjects = this.activatedRoute.snapshot.data['schoolSubjects'];
    this.schools = this.activatedRoute.snapshot.data['schools'];
    //console.log('this.schools: ', this.schools);
    //console.log('this.schoolSubjects: ', this.schoolSubjects);
    this.terms = this.activatedRoute.snapshot.data['terms'];
    this.durations = this.activatedRoute.snapshot.data['durations'];

    this.applicationSteps = this.applicationService.getApplicationSteps();

    this.applicationService.isCovid19VaccinationNeeded(this.applicationId)
      .pipe(
        tap(x => this.isCovid19VaccinationNeeded = x),
        switchMap(x => this.isCovid19VaccinationNeeded ? this.lookupService.getDeiApprovedCovid19Vaccines() : of({Errors: null, Result: []})),
        tap(x => this.deiApprovedCovid19Vaccines = x.Result)
      ).subscribe();

    updateEmptyValuesForNullUndefined(this.summary);

    this.applicationService.getApplicationSections(this.applicationId)
      .subscribe((response: ApiResponse) => {
        this.updateSectionCompletionStatus();
        this.isPaymentReady = !this.sections.some(section => section.completionStatus === false);

      }, err => {
        console.error(err);
      });

    this.applicationService.getApplicationDetails(this.applicationId)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {
          //console.log("Application details are -->", response);
          this.isSubmittedApplication = response.Result.ApplicationStatus === 'Submitted';

        }
      }, err => {
        console.error(err);
      });
      

    this.collapseAll();

  }

  toggleSectionVisibilityStatus(sectionName: string) {
    const section = this.sectionsMappedByName[sectionName];

    if (section) {
      section.visiblilityStatus = !section.visiblilityStatus;
    }
  }

  expandAll() {
    this.sections.forEach(section => section.visiblilityStatus = true);
  }

  collapseAll() {
    this.sections.forEach(section => section.visiblilityStatus = false);
  }



  getYesNo(val: any): string {
    if (val === null || val === '') return '';
    return val === true ? 'Yes' : 'No';
  }

  getMobilePhoneNumber(): string {

    let mobilePhoneNumber = '';

    if (this.summary.StudentDetails.MobileCountryCode
      && this.summary.StudentDetails.MobilePhoneNumber) {
      mobilePhoneNumber = `${this.summary.StudentDetails.MobileCountryCode} ${this.summary.StudentDetails.MobilePhoneNumber}`;
    }

    return mobilePhoneNumber;
  }

  updateSectionCompletionStatus() {

    for (const section of this.sections) {
      const appSection = this.applicationSteps.find(appStep => appStep.Section === section.name);
      section.completionStatus = appSection.Status === 'Complete';
    }

  }

  getDate(dateInString: string): string {
    if (!dateInString) {
      return null;
    }

    const date = new Date(dateInString);
    const dateStr = date ? `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}` : '';
    return dateStr;
  }

  getCountryById(countryId: string): string {
    //console.log('countryId', countryId);
    const selectedCountry = this.countries.find(c => c.Id === countryId);
    //console.log('selectedCountry', selectedCountry);
    const countryDescription = selectedCountry ? selectedCountry.Description : '';
    //console.log('countryDescription', countryDescription);
    return selectedCountry ? selectedCountry.Description : '';
  }

  getGender(genderValue: string): string {
    if (genderValue === 'M') {
      return 'Male';
    } else if (genderValue === 'F') {
      return 'Female';
    }
    return '';
  }

  getRelationship(relationshipCode: string): string {
    const selectedRelationship = this.relationships.find(r => r.Code === relationshipCode);
    return selectedRelationship ? selectedRelationship.Description : '';
  }

  getAccommodationType(accommodation: AccommodationWelfareDetails): string {
    if (accommodation && accommodation.AccommodationTypeId) {
      switch (accommodation.AccommodationTypeId) {
        case 1:
          return 'Parent, will accompany the student on a guardian visa.';
        case 2:
          return 'Nominated Department of Home Affairs approved relative to provide accommodation, welfare and airport reception. (Direct Relative)';
        case 3:
          return `Nominated a person over 25 years of age residing in NSW to provide accommodation, welfare and airport reception. 
          Requested the NSW Department of Education to approve these arrangements as the welfare provider. (Parent Nomination)`;
        case 4:
          return `Requested the NSW Department of Education to arrange accommodation, welfare and airport reception. (Homestay)`;
        default:
          return '';
      }
    }
    return '';
  }

  getSchoolProgram(schoolProgramType: string): string {

    switch (schoolProgramType) {
      case 'International':
        return 'International Student Program';
      case 'StudyAbroad':
        return 'Study Abroad Program';
      default:
        return '';
    }
  }

  getSchoolProgramStartDate(schoolProgram: SchoolProgramPlacementDetails) {
    if (schoolProgram && schoolProgram.StartTermCode && schoolProgram.StartTermYear)
      return schoolProgram.StartTermDescription + ', ' + schoolProgram.StartTermYear;
    return '';
  }
  isPrimaryYear() {
    if (this.summary.SchoolProgram && this.summary.SchoolProgram.YearOfEntryCode) {
      const yearOfEntryCode = this.summary.SchoolProgram.YearOfEntryCode;
      return yearOfEntryCode === 'Kindergarten'
        || yearOfEntryCode === 'Year1'
        || yearOfEntryCode === 'Year2'
        || yearOfEntryCode === 'Year3'
        || yearOfEntryCode === 'Year4'
        || yearOfEntryCode === 'Year5'
        || yearOfEntryCode === 'Year6';
    }
    return false;
  }

  get Carer() {
    var selectedCarer = this.summary.Accommodation.Carers.length ? this.summary.Accommodation.Carers.find(c => c.CarerId === this.summary.Accommodation.SelectedCarerId) : null;

    if (!selectedCarer) {
      return {
        Description: '',
        GivenName: '',
        FamilyName: ''
      }
    }

    return selectedCarer;
  }

  getPreferredSchoolName(preferredSchoolId: string): string {
    if (this.schools) {
      const school = this.schools.find(school => school.Id == preferredSchoolId);
      if (school) {
        return school.Name;
      }
    }
    return '';
  }

  showOrHideParentContactDetails(parentGuardian: ParentGuardianDetails): boolean {
    let showDetails: boolean = true;
    if (parentGuardian.IsParent && (parentGuardian.IsDeceased || !parentGuardian.HasLegalCustody)) {
      showDetails = false;
    }
    return showDetails;
  }

  editParentGuardian(parentGuardianId: number) {
    this.applicationService.IsParentOrLegalcustodian(this.applicationId, parentGuardianId)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {
          if (response.Result.IsParentOrLegalcustodian.Result) {
            this.router.navigate(['/applications', this.applicationId, 'edit', 'parent-guardian-details', 'parent-legalcustodian']);
          } else {
            this.router.navigate(['/applications', this.applicationId, 'edit', 'parent-guardian-details', parentGuardianId, 'edit']);
          }
        }
      }, (errorResponse: any) => {
        //console.log('editParentGuardian-IsParentOrLegalcustodian - Error');
        console.log(errorResponse);
      });
  }

  next() {
    const userType = localStorage.getItem('userType');

    if (userType === 'agent') {      
      this.router.navigate(['/applications', this.applicationId, 'edit', 'agent-details']);
    } else {
      this.router.navigate(['/applications', this.applicationId, 'edit', 'payment']);
    }
  }

  previous() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'supporting-documents']);
  }

  //show border when tab on link for accessibility
  linkTabAccessibility(event): void {
    event.style.outline = "1px solid black";
  }

  linkBlurAccessibility(event): void {
    event.style.outline = "";
  }

  printPage(event): void {
    event.preventDefault();
    window.print();
  }

  @HostListener("window:beforeprint", [])
  onWindowBeforePrint() {
    this.titleService.setTitle(Tab_Title.OAP_Print_Title);
  }

  @HostListener("window:afterprint", [])
  onWindowAfterPrint() {
    if (localStorage.getItem("userType") == "agent") {
      this.titleService.setTitle(Tab_Title.Agent_Summary_Title);
    }else{
      this.titleService.setTitle(Tab_Title.Summary_Title);
    }
  }
}
